import { ref } from 'vue'
import axios from 'axios'

export interface AITranslateField {
  path: string
  label: string
  value: string
  preview?: string
}

export interface AITranslateSource {
  kind: string
  media_id?: number
}

export interface AITranslateAvailableSource {
  kind: string
  label: string
  available: boolean
}

export interface GetTranslatableFieldsResult {
  fields: AITranslateField[]
  source: AITranslateSource
  available_sources: AITranslateAvailableSource[]
  suggested_lang?: string
}

export interface PreviewMediaFieldsResult {
  previews: Record<string, string>
  source: AITranslateSource
}

export interface ApplyMediaFieldsResult {
  success: boolean
  applied: string[]
}

export default function useAITranslate() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const getTranslatableFields = async (
    vhostId: string,
    mediaId: number,
    sourceKind?: string,
  ): Promise<GetTranslatableFieldsResult | null> => {
    loading.value = true
    error.value = null
    try {
      const payload: Record<string, unknown> = {
        method: 'aiTranslate.getTranslatableFields',
        vhostID: Number(vhostId),
        mediaID: mediaId,
      }
      if (sourceKind) payload.source_kind = sourceKind
      const response = await axios.post('/services/json', payload)
      if (!response.data || response.data.status !== true) {
        error.value = response.data?.message || 'Failed to load fields'
        return null
      }
      return response.data.result as GetTranslatableFieldsResult
    } catch (err: any) {
      error.value = err.message || 'Failed to load fields'
      return null
    } finally {
      loading.value = false
    }
  }

  const previewMediaFields = async (
    vhostId: string,
    mediaId: number,
    targetLang: string,
    fieldPaths: string[],
    sourceKind?: string,
  ): Promise<PreviewMediaFieldsResult | null> => {
    loading.value = true
    error.value = null
    try {
      const payload: Record<string, unknown> = {
        method: 'aiTranslate.previewMediaFields',
        vhostID: Number(vhostId),
        mediaID: mediaId,
        target_lang: targetLang,
        field_paths: fieldPaths,
      }
      if (sourceKind) payload.source_kind = sourceKind
      const response = await axios.post('/services/json', payload)
      if (!response.data || response.data.status !== true) {
        error.value = response.data?.message || 'Preview failed'
        return null
      }
      return response.data.result as PreviewMediaFieldsResult
    } catch (err: any) {
      error.value = err.message || 'Preview failed'
      return null
    } finally {
      loading.value = false
    }
  }

  const applyMediaFields = async (
    vhostId: string,
    mediaId: number,
    targetLang: string,
    fieldPaths: string[],
    sourceKind?: string,
  ): Promise<ApplyMediaFieldsResult | null> => {
    loading.value = true
    error.value = null
    try {
      const payload: Record<string, unknown> = {
        method: 'aiTranslate.applyMediaFields',
        vhostID: Number(vhostId),
        mediaID: mediaId,
        target_lang: targetLang,
        field_paths: fieldPaths,
      }
      if (sourceKind) payload.source_kind = sourceKind
      const response = await axios.post('/services/json', payload)
      if (!response.data || response.data.status !== true) {
        error.value = response.data?.message || 'Apply failed'
        return null
      }
      return response.data.result as ApplyMediaFieldsResult
    } catch (err: any) {
      error.value = err.message || 'Apply failed'
      return null
    } finally {
      loading.value = false
    }
  }

  const renderLangTemplate = async (
    vhostId: string,
    mediaId: number,
    targetLang: string,
  ): Promise<string | null> => {
    loading.value = true
    error.value = null
    try {
      const response = await axios.post('/services/json', {
        method: 'aiTranslate.renderLangTemplateForMedia',
        vhostID: Number(vhostId),
        mediaID: mediaId,
        target_lang: targetLang,
      })
      if (!response.data || response.data.status !== true) {
        error.value = response.data?.message || 'Render failed'
        return null
      }
      return response.data.result as string
    } catch (err: any) {
      error.value = err.message || 'Render failed'
      return null
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getTranslatableFields,
    previewMediaFields,
    applyMediaFields,
    renderLangTemplate,
  }
}
